import { SystemConfiguration } from '../../types/cms.types';
import { SystemConfigurationService, SystemConfigFilter, CreateSystemConfigRequest, UpdateSystemConfigRequest } from '../../service/supabase/SystemConfigurationService';
import IBloc from '../../base/IBloc';

export interface SystemConfigViewState {
    systemConfigurations: SystemConfiguration[];
    pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
    loading: boolean;
    error: string | null;
    filters: SystemConfigFilter;
    hasData: boolean;
    currentConfig: SystemConfiguration | null;
}

export class BlocManagementSystemConfiguration extends IBloc {
    private systemConfigService: SystemConfigurationService;

    /**
     * State chính của System Configuration Management
     */
    info: SystemConfigViewState = {
        systemConfigurations: [],
        pagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0
        },
        loading: false,
        error: null,
        filters: {},
        hasData: false,
        currentConfig: null
    };

    constructor() {
        super();
        this.systemConfigService = new SystemConfigurationService();
    }

    /**
     * Khởi tạo dữ liệu
     */
    async initData(): Promise<void> {
        try {
            this.setLoading(true);
            this.setError(null);

            // Load system configurations và current config song song
            const [systemConfigsResult, currentConfig] = await Promise.all([
                this.systemConfigService.getSystemConfigurations(1, 10),
                this.systemConfigService.getCurrentSystemConfiguration()
            ]);

            this.updateViewState({
                systemConfigurations: systemConfigsResult.data,
                pagination: {
                    total: systemConfigsResult.total,
                    page: systemConfigsResult.page,
                    limit: systemConfigsResult.limit,
                    totalPages: systemConfigsResult.totalPages
                },
                hasData: systemConfigsResult.data.length > 0,
                currentConfig: currentConfig
            });

        } catch (error) {
            console.error('Error in initData:', error);
            this.setError(error instanceof Error ? error.message : 'Có lỗi xảy ra khi tải dữ liệu');
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Tải lại dữ liệu system configurations
     */
    async loadSystemConfigurations(page?: number, limit?: number): Promise<void> {
        try {
            this.setLoading(true);
            this.setError(null);

            const currentPage = page || this.info.pagination.page;
            const currentLimit = limit || this.info.pagination.limit;

            const result = await this.systemConfigService.getSystemConfigurations(
                currentPage,
                currentLimit,
                this.info.filters
            );

            this.updateViewState({
                systemConfigurations: result.data,
                pagination: {
                    total: result.total,
                    page: result.page,
                    limit: result.limit,
                    totalPages: result.totalPages
                },
                hasData: result.data.length > 0
            });

        } catch (error) {
            console.error('Error in loadSystemConfigurations:', error);
            this.setError(error instanceof Error ? error.message : 'Có lỗi xảy ra khi tải dữ liệu');
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Thay đổi trang
     */
    async changePage(page: number): Promise<void> {
        if (page === this.info.pagination.page) return;
        await this.loadSystemConfigurations(page, this.info.pagination.limit);
    }

    /**
     * Thay đổi kích thước trang
     */
    async changePageSize(limit: number): Promise<void> {
        await this.loadSystemConfigurations(1, limit);
    }

    /**
     * Tìm kiếm system configurations
     */
    async searchSystemConfigurations(searchTerm: string): Promise<void> {
        try {
            this.setLoading(true);
            this.setError(null);

            const filters = {
                ...this.info.filters,
                search: searchTerm
            };

            const result = await this.systemConfigService.getSystemConfigurations(1, this.info.pagination.limit, filters);

            this.updateViewState({
                systemConfigurations: result.data,
                pagination: {
                    total: result.total,
                    page: 1,
                    limit: this.info.pagination.limit,
                    totalPages: result.totalPages
                },
                filters: filters,
                hasData: result.data.length > 0
            });

        } catch (error) {
            console.error('Error in searchSystemConfigurations:', error);
            this.setError(error instanceof Error ? error.message : 'Có lỗi xảy ra khi tìm kiếm');
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Filter theo app name
     */
    async filterByAppName(appName: string): Promise<void> {
        try {
            this.setLoading(true);
            this.setError(null);

            const filters = {
                ...this.info.filters,
                app_name: appName || undefined
            };

            const result = await this.systemConfigService.getSystemConfigurations(1, this.info.pagination.limit, filters);

            this.updateViewState({
                systemConfigurations: result.data,
                pagination: {
                    total: result.total,
                    page: 1,
                    limit: this.info.pagination.limit,
                    totalPages: result.totalPages
                },
                filters: filters,
                hasData: result.data.length > 0
            });

        } catch (error) {
            console.error('Error in filterByAppName:', error);
            this.setError(error instanceof Error ? error.message : 'Có lỗi xảy ra khi lọc dữ liệu');
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Xóa bộ lọc
     */
    async clearFilters(): Promise<void> {
        try {
            this.setLoading(true);
            this.setError(null);

            const result = await this.systemConfigService.getSystemConfigurations(1, this.info.pagination.limit, {});

            this.updateViewState({
                systemConfigurations: result.data,
                pagination: {
                    total: result.total,
                    page: 1,
                    limit: this.info.pagination.limit,
                    totalPages: result.totalPages
                },
                filters: {},
                hasData: result.data.length > 0
            });

        } catch (error) {
            console.error('Error in clearFilters:', error);
            this.setError(error instanceof Error ? error.message : 'Có lỗi xảy ra khi xóa bộ lọc');
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Làm mới dữ liệu
     */
    async refresh(): Promise<void> {
        await this.loadSystemConfigurations(this.info.pagination.page, this.info.pagination.limit);
    }

    /**
     * Tạo system configuration mới
     */
    async createSystemConfiguration(request: CreateSystemConfigRequest): Promise<void> {
        try {
            await this.systemConfigService.createSystemConfiguration(request);
            await this.refresh();
        } catch (error) {
            console.error('Error in createSystemConfiguration:', error);
            throw error;
        }
    }

    /**
     * Cập nhật system configuration
     */
    async updateSystemConfiguration(request: UpdateSystemConfigRequest): Promise<void> {
        try {
            await this.systemConfigService.updateSystemConfiguration(request);
            await this.refresh();
        } catch (error) {
            console.error('Error in updateSystemConfiguration:', error);
            throw error;
        }
    }

    /**
     * Xóa system configuration
     */
    async deleteSystemConfiguration(id: string): Promise<void> {
        try {
            await this.systemConfigService.deleteSystemConfiguration(id);
            await this.refresh();
        } catch (error) {
            console.error('Error in deleteSystemConfiguration:', error);
            throw error;
        }
    }

    /**
     * Lấy thông tin chi tiết system configuration
     */
    async getSystemConfigurationById(id: string): Promise<SystemConfiguration | null> {
        try {
            return await this.systemConfigService.getSystemConfigurationById(id);
        } catch (error) {
            console.error('Error in getSystemConfigurationById:', error);
            throw error;
        }
    }

    /**
     * Lấy current system configuration
     */
    async getCurrentSystemConfiguration(): Promise<void> {
        try {
            const currentConfig = await this.systemConfigService.getCurrentSystemConfiguration();
            this.updateViewState({
                currentConfig: currentConfig
            });
        } catch (error) {
            console.error('Error in getCurrentSystemConfiguration:', error);
            throw error;
        }
    }

    /**
     * Helper methods
     */
    private setLoading(loading: boolean): void {
        this.updateViewState({ loading });
    }

    private setError(error: string | null): void {
        this.updateViewState({ error });
    }

    private updateViewState(updates: Partial<SystemConfigViewState>): void {
        this.info = { ...this.info, ...updates };
        this.setStream('info', this.info);
    }

    get currentPagination() {
        return this.info.pagination;
    }

    /**
     * Cleanup
     */
    dispose(): void {
        super.dispose();
    }
}